import {
  BED_OPTIONS,
  BED_TYPES,
  type BedDto,
  type BedOption,
  type BedType,
  defaultSizeForBedType,
  type GuestDto,
  sleepsForBedType,
} from '@room-mates/shared';
import { type FormEvent, type ReactElement, useState } from 'react';
import { useFieldErrors } from '../../shared/hooks/useFieldErrors.js';
import { BedIllustration } from '../../shared/illustrations/BedIllustration.js';
import { bedKindLabel } from '../../shared/i18n/dictionary.js';
import { useTranslation } from '../../shared/i18n/useLanguage.js';
import { Button, Dialog, ErrorAlert, Field, Select, TextInput } from '../../shared/ui/index.js';
import { useCreateBed, useUpdateBed } from './houseHooks.js';

export interface BedDialogProps {
  readonly houseId: string;
  readonly roomId: string;
  /** Absent when adding a bed. */
  readonly bed?: BedDto;
  readonly guests: readonly GuestDto[];
  readonly onClose: () => void;
}

/**
 * One dialog for adding and for editing a bed.
 *
 * Width is the interesting field: it tracks the bed type until the user types
 * their own number, which is exactly the rule the domain enforces, so the form
 * shows that state rather than hiding it behind a pre-filled box.
 */
export function BedDialog({ houseId, roomId, bed, guests, onClose }: BedDialogProps): ReactElement {
  const t = useTranslation();
  const createBed = useCreateBed(houseId, roomId);
  const updateBed = useUpdateBed(houseId, roomId);
  const mutation = bed === undefined ? createBed : updateBed;

  const [type, setType] = useState<BedType>(bed?.type ?? 'single');
  const [option, setOption] = useState<BedOption>(bed?.option ?? 'standard');
  const [customSize, setCustomSize] = useState<string>(
    bed?.sizeIsCustom === true ? String(bed.sizeCm) : '',
  );
  const [favoriteGuestIds, setFavoriteGuestIds] = useState<string[]>([
    ...(bed?.favoriteGuestIds ?? []),
  ]);
  const fieldError = useFieldErrors(mutation.error);

  const effectiveSize = customSize.trim() === '' ? defaultSizeForBedType(type) : Number(customSize);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    const sizeCm = customSize.trim() === '' ? null : Number(customSize);
    const payload = { type, option, sizeCm, favoriteGuestIds };

    if (bed === undefined) {
      createBed.mutate(payload, { onSuccess: onClose });
      return;
    }
    updateBed.mutate({ bedId: bed.id, input: payload }, { onSuccess: onClose });
  };

  const moveFavorite = (index: number, delta: number): void => {
    const next = [...favoriteGuestIds];
    const target = index + delta;
    const moved = next[index];
    const displaced = next[target];
    if (moved === undefined || displaced === undefined) {
      return;
    }
    next[index] = displaced;
    next[target] = moved;
    setFavoriteGuestIds(next);
  };

  const available = guests.filter((guest) => !favoriteGuestIds.includes(guest.id));

  return (
    <Dialog
      title={
        bed === undefined
          ? t.bedDialog.addTitle
          : t.bedDialog.editTitle(bedKindLabel(t, bed.type, bed.option))
      }
      onClose={onClose}
    >
      <form className="stack" onSubmit={submit} noValidate>
        <ErrorAlert error={mutation.error} />
        {bed === undefined ? null : <p className="field__hint">{bed.name}</p>}

        <div className="row" style={{ justifyContent: 'center' }}>
          <BedIllustration type={type} option={option} size="lg" />
        </div>

        <Field label={t.bedDialog.typeLabel} error={fieldError('type')}>
          {(props) => (
            <Select
              {...props}
              value={type}
              onChange={(event) => setType(event.target.value as BedType)}
            >
              {BED_TYPES.map((value) => (
                <option key={value} value={value}>
                  {t.bedDialog.typeOption(
                    t.catalog.bedType[value],
                    defaultSizeForBedType(value),
                    sleepsForBedType(value),
                  )}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label={t.bedDialog.optionLabel} error={fieldError('option')}>
          {(props) => (
            <Select
              {...props}
              value={option}
              onChange={(event) => setOption(event.target.value as BedOption)}
            >
              {BED_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {t.catalog.bedOption[value]}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field
          label={t.bedDialog.widthLabel}
          hint={
            customSize.trim() === ''
              ? t.bedDialog.widthHintDefault(t.catalog.bedType[type], effectiveSize)
              : t.bedDialog.widthHintCustom
          }
          error={fieldError('sizeCm')}
        >
          {(props) => (
            <TextInput
              {...props}
              type="number"
              min={60}
              max={250}
              placeholder={String(defaultSizeForBedType(type))}
              value={customSize}
              onChange={(event) => setCustomSize(event.target.value)}
            />
          )}
        </Field>

        <div className="field">
          <span className="field__label">{t.bedDialog.favoriteGuestsLabel}</span>
          <span className="field__hint">{t.bedDialog.favoriteGuestsHint}</span>

          {favoriteGuestIds.length > 0 ? (
            <ol className="data-list">
              {favoriteGuestIds.map((guestId, index) => (
                <li key={guestId} className="list-row">
                  <span className="list-row__title">
                    {index + 1}.{' '}
                    {guests.find((guest) => guest.id === guestId)?.name ?? t.bedDialog.unknown}
                  </span>
                  <span className="row">
                    <Button
                      small
                      variant="ghost"
                      disabled={index === 0}
                      aria-label={t.bedDialog.moveUp}
                      onClick={() => moveFavorite(index, -1)}
                    >
                      ↑
                    </Button>
                    <Button
                      small
                      variant="ghost"
                      disabled={index === favoriteGuestIds.length - 1}
                      aria-label={t.bedDialog.moveDown}
                      onClick={() => moveFavorite(index, 1)}
                    >
                      ↓
                    </Button>
                    <Button
                      small
                      variant="danger"
                      onClick={() =>
                        setFavoriteGuestIds(favoriteGuestIds.filter((id) => id !== guestId))
                      }
                    >
                      {t.bedDialog.remove}
                    </Button>
                  </span>
                </li>
              ))}
            </ol>
          ) : (
            <p className="field__hint">{t.guestDialog.nobodyYet}</p>
          )}

          {available.length > 0 ? (
            <Select
              aria-label={t.bedDialog.addAFavoriteGuest}
              value=""
              onChange={(event) => {
                if (event.target.value !== '') {
                  setFavoriteGuestIds([...favoriteGuestIds, event.target.value]);
                }
              }}
            >
              <option value="">{t.bedDialog.addGuestPlaceholder}</option>
              {available.map((guest) => (
                <option key={guest.id} value={guest.id}>
                  {guest.name}
                </option>
              ))}
            </Select>
          ) : null}
        </div>

        <div className="row row--end">
          <Button variant="secondary" onClick={onClose}>
            {t.common.cancel}
          </Button>
          <Button type="submit" loading={mutation.isPending}>
            {bed === undefined ? t.bedDialog.addBed : t.bedDialog.saveBed}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
